import { motion, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import PricingStageTimeline from "@/components/PricingStageTimeline";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
import { buildMusicEventSchema } from "@/lib/event-schema";
import Footer from "@/components/Footer";
import SponsorsBlock from "@/components/SponsorsBlock";
import TicketsUrgencyBar from "@/components/TicketsUrgencyBar";
import { Check, Star, Anchor, Music, Utensils, GlassWater, PartyPopper, Crown } from "lucide-react";

// ── Queue Config ────────────────────────────────────────────
const QUEUE_ENABLED = false;
const MIN_QUEUE_POSITION = 180;
const MAX_QUEUE_POSITION = 320;
const TICK_INTERVAL_MIN = 18000;
const TICK_INTERVAL_MAX = 28000;
const TICK_DROP_MIN = 50;
const TICK_DROP_MAX = 90;
// ────────────────────────────────────────────────────────────

// Wave 2 countdown logic lives in Wave2CountdownAlert.tsx

function rand(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function estimatedWait(pos: number) {
  if (pos > 200) return "under 3 minutes";
  if (pos > 100) return "under 2 minutes";
  return "under 1 minute";
}

const PulsingDots = () => (
  <div className="flex items-center justify-center gap-1.5 my-6">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="block w-2 h-2 rounded-full bg-[hsl(var(--yacht-charcoal)/0.3)]"
        style={{
          animation: "pulse-dot 1.4s ease-in-out infinite",
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes pulse-dot {
        0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
        40% { opacity: 1; transform: scale(1); }
      }
    `}</style>
  </div>
);

const images = {
  hero1: PHOTOS.STAGE.bandDramaticLights,
  hero2: PHOTOS.CROWD.audienceHandsRaised,
};

const gaFeatures = [
  { icon: Music, text: "All 9 national acts + DJs + local bands across both days" },
  { icon: PartyPopper, text: "The Floating DJ Deck — yes, literally on the Detroit River" },
  { icon: Anchor, text: "Run of the Roostertail: Marine Room, riverfront patio, every outdoor space" },
  { icon: Utensils, text: "Themed food & drink available for purchase across both floors, both days (cocktails, beer, wine, snacks)" },
];

const vipFeatures = [
  { icon: Crown, text: "Roostertail Lounge — your private home base with comfort & seating" },
  { icon: Star, text: "Priority entry — skip the line at the gate, both days" },
  { icon: Music, text: "All 9 national acts + DJs + local bands" },
  { icon: PartyPopper, text: "The Floating DJ Deck — yes, literally on the Detroit River" },
  { icon: Anchor, text: "Run of all open areas — including the Marine Room and Palm River Room, both days" },
];

const TicketAccordionRow = ({
  tier,
  subLabel,
  features,
  isVip = false,
  isOpen,
  onToggle,
  onBuy,
  isDisabled = false,
  delay = 0,
  customBody,
  newBadge = false,
}: {
  tier: string;
  subLabel: string;
  features?: { icon: React.ElementType; text: string }[];
  isVip?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onBuy: () => void;
  isDisabled?: boolean;
  delay?: number;
  customBody?: React.ReactNode;
  newBadge?: boolean;
}) => {
  return (
    <motion.div
      className="relative overflow-hidden rounded-2xl bg-yacht-charcoal border border-yacht-charcoal/15"
      style={{ boxShadow: "0 12px 32px -16px rgba(0,0,0,0.35), 0 4px 12px -6px rgba(0,0,0,0.18)" }}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay }}
    >
      <span
        className={`absolute left-0 top-0 bottom-0 w-1 md:w-1.5 ${
          isDisabled ? "bg-white/15" : "bg-yacht-yellow"
        }`}
        aria-hidden
      />

      <button
        onClick={isDisabled ? undefined : onToggle}
        disabled={isDisabled}
        className={`w-full flex items-center justify-between gap-3 py-6 md:py-8 pl-6 md:pl-10 pr-5 md:pr-8 transition-colors text-left ${
          isDisabled
            ? "opacity-60 cursor-not-allowed"
            : "hover:bg-white/5 cursor-pointer"
        }`}
        aria-expanded={isOpen}
      >
        <span className="flex flex-col gap-1 md:flex-row md:items-baseline md:gap-5 min-w-0">
          <span
            className={`text-xl sm:text-2xl md:text-4xl font-bold tracking-tight uppercase leading-tight ${
              isDisabled ? "text-white/60" : "text-white"
            }`}
          >
            {tier}
          </span>
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] sm:text-[11px] md:text-sm font-semibold tracking-wider whitespace-nowrap text-white/55">
              {subLabel}
            </span>
            {newBadge && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yacht-yellow text-yacht-charcoal text-[9px] md:text-[10px] font-bold uppercase tracking-[0.15em] whitespace-nowrap">
                Out Now
              </span>
            )}
          </span>
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-3xl md:text-4xl font-light leading-none w-6 md:w-7 text-center flex-shrink-0"
          style={{ color: isDisabled ? "rgba(255,255,255,0.4)" : "#F7B41C" }}
        >
          {isDisabled ? "—" : "+"}
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && !isDisabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="px-6 md:px-10 pb-8 md:pb-10">
              {customBody ? (
                <div className="max-w-3xl">
                  {customBody}
                </div>
              ) : (
              <div className="max-w-2xl">
              {isVip && (
                <div className="mb-6 rounded-xl bg-yacht-yellow/10 border border-yacht-yellow/20 p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <GlassWater className="w-5 h-5 text-yacht-yellow" />
                    <span className="text-yacht-yellow font-bold text-sm uppercase tracking-wide">
                      Drinks + Food in the VIP Lounge
                    </span>
                  </div>
                  <p className="text-white/80 text-xs md:text-sm leading-relaxed">
                    Complimentary drinks and food are served only inside the Roostertail VIP Lounge — your VIP-only home base — both days. Anywhere else in the venue, food and drinks are available for purchase.
                  </p>
                </div>
              )}

              <ul className="space-y-3 md:space-y-4 mb-7">
                {(features ?? []).map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-yacht-yellow/20 flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-yacht-yellow" strokeWidth={3} />
                    </span>
                    <span className="text-sm md:text-base text-white/85 leading-relaxed">
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={onBuy}
                className="w-full md:w-auto md:px-14 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base md:text-lg hover:brightness-110 transition-all cursor-pointer"
                style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
              >
                Get Tickets →
              </button>
              </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const fridayPreview = ["Yacht Rock Revue", "Pablo Cruise", "Seven Wonders"];
const saturdayPreview = ["Three Dog Night", "Ambrosia", "Party Pupils", "Walter Egan"];

const SingleDayBody = ({
  onBuyFriday,
  onBuySaturday,
}: {
  onBuyFriday: () => void;
  onBuySaturday: () => void;
}) => (
  <div className="space-y-5">
    <p className="text-white/65 text-xs md:text-sm leading-relaxed">
      Pick your day. Full venue access, every act on that day, the Floating DJ Deck — all included.
      GA and VIP options shown at checkout.
    </p>

    <div className="grid sm:grid-cols-2 gap-3">
      <DayCard
        day="Friday"
        date="July 17"
        hours="3 PM – Midnight"
        actsCount="3 national acts + DJs + local bands"
        previewActs={fridayPreview}
        onBuy={onBuyFriday}
        accent="blue"
      />
      <DayCard
        day="Saturday"
        date="July 18"
        hours="Noon – Midnight"
        actsCount="6 national acts + Party Pupils + DJs + local bands"
        previewActs={saturdayPreview}
        onBuy={onBuySaturday}
        accent="gold"
        badge="Headliner: Three Dog Night"
      />
    </div>

    <p className="text-white/45 text-[11px] md:text-xs text-center leading-relaxed">
      Prices rise as each batch sells through · 2-day pass = best value if you can do both
    </p>
  </div>
);

const DayCard = ({
  day,
  date,
  hours,
  actsCount,
  previewActs,
  onBuy,
  accent,
  badge,
}: {
  day: "Friday" | "Saturday";
  date: string;
  hours: string;
  actsCount: string;
  previewActs: string[];
  onBuy: () => void;
  accent: "blue" | "gold";
  badge?: string;
}) => {
  const isGold = accent === "gold";
  return (
    <div
      className={`relative rounded-xl p-5 flex flex-col gap-4 border ${
        isGold
          ? "bg-yacht-yellow/[0.07] border-yacht-yellow/30"
          : "bg-white/[0.04] border-white/15"
      }`}
    >
      {badge && (
        <span className="absolute -top-2 left-4 inline-flex items-center px-2 py-0.5 rounded-full bg-yacht-yellow text-yacht-charcoal text-[9px] font-bold uppercase tracking-[0.15em] whitespace-nowrap">
          {badge}
        </span>
      )}

      <div>
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <h4
            className={`text-white font-bold text-lg md:text-xl uppercase tracking-tight ${
              isGold ? "" : ""
            }`}
          >
            {day}
          </h4>
        </div>
        <p className="text-white/55 text-[11px] uppercase tracking-wider font-semibold">
          {date} · {hours}
        </p>
        <p
          className={`text-[12px] mt-1.5 font-semibold ${
            isGold ? "text-yacht-yellow" : "text-yacht-blue brightness-150"
          }`}
        >
          {actsCount}
        </p>
      </div>

      <ul className="space-y-1.5">
        {previewActs.map((name) => (
          <li
            key={name}
            className="flex items-center gap-2 text-white/80 text-[13px] leading-tight"
          >
            <Music className="w-3 h-3 text-yacht-yellow flex-shrink-0" />
            {name}
          </li>
        ))}
      </ul>

      <button
        onClick={onBuy}
        className={`mt-auto w-full rounded-full py-3 px-5 font-bold text-sm uppercase tracking-wide transition-all flex items-center justify-center gap-1.5 ${
          isGold
            ? "bg-yacht-yellow text-yacht-charcoal hover:brightness-110"
            : "bg-white/10 text-white border border-white/30 hover:bg-white/20"
        }`}
        style={
          isGold
            ? { boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }
            : undefined
        }
      >
        Get {day} Tickets →
      </button>
    </div>
  );
};

const Tickets = () => {
  const ticketsJsonLd = useMemo(() => buildMusicEventSchema({
    url: "https://yachtrockdetroit.com/tickets",
    description: "Get tickets for Yacht Rock Detroit 2026 — Detroit's two-day waterfront music festival at The Roostertail, July 17-18. General Admission $219, VIP Admiral's Pass $450, single-day passes available. Live concerts by Three Dog Night, Pablo Cruise, Ambrosia, and 6 more acts on the Detroit River.",
  }), []);

  usePageSEO({
    title: "Tickets — Yacht Rock Detroit 2026 | Concert Tickets Detroit",
    description: "Buy tickets for Yacht Rock Detroit 2026. Two-day waterfront concert festival at The Roostertail in Detroit — July 17-18. GA $219 / VIP $450 / single-day passes available. Three Dog Night, Pablo Cruise, Ambrosia & more live on the Detroit River.",
    keywords: "yacht rock detroit tickets, concert tickets detroit, detroit festival tickets 2026, summer concert tickets detroit, roostertail detroit tickets, detroit waterfront concert tickets, yacht rock concert tickets",
    ogImage: PHOTOS.CROWD.sunsetStageView,
    jsonLd: ticketsJsonLd,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Tickets", url: "https://yachtrockdetroit.com/tickets" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  // ── Accordion state ──
  const [openTier, setOpenTier] = useState<"ga" | "vip" | "single" | null>("single");

  // ── Value modal state ──

  // ── Queue state ──
  const [phase, setPhase] = useState<"idle" | "loading" | "queue" | "cleared">("idle");
  const [position, setPosition] = useState(0);
  const [startPosition, setStartPosition] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const skipQueue = new URLSearchParams(window.location.search).get("skip") === "true";

  const suppressModalZIndex = useCallback(() => {
    const observer = new MutationObserver(() => {
      document.querySelectorAll('body > div, body > iframe').forEach(el => {
        const style = window.getComputedStyle(el);
        const z = parseInt(style.zIndex);
        if (z >= 99999 && !el.closest('.queue-overlay')) {
          (el as HTMLElement).style.setProperty('z-index', '99998', 'important');
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  }, []);

  // Tickets is the #1 page we WANT indexed — explicitly allow it (overrides any
  // stale robots meta) so search engines rank it for "Detroit concert tickets" etc.
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "index, follow, max-image-preview:large";
  }, []);

  // Load OnTourHQ modal script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://events.ontourhq.com/ts_modal.js";
    script.async = true;
    script.onload = () => {
      (window as any).TSModals?.buildModal({
        url: "https://events.ontourhq.com/e/yrd/tickets",
        modalTriggerElementId: "modal-trigger-element-id",
      });
    };
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const scheduleTick = useCallback(() => {
    const interval = rand(TICK_INTERVAL_MIN, TICK_INTERVAL_MAX);
    timerRef.current = setTimeout(() => {
      setPosition((prev) => {
        const drop = rand(TICK_DROP_MIN, TICK_DROP_MAX);
        const next = Math.max(0, prev - drop);
        if (next > 0) scheduleTick();
        return next;
      });
    }, interval);
  }, []);

  // When queue reaches 0, transition to cleared
  useEffect(() => {
    if (phase === "queue" && position === 0 && startPosition > 0) {
      setPhase("cleared");
    }
  }, [phase, position, startPosition]);

  // Cleared → idle after 2s
  useEffect(() => {
    if (phase !== "cleared") return;
    const t = setTimeout(() => {
      setPhase("idle");
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
        document.querySelectorAll('body > div, body > iframe').forEach(el => {
          if ((el as HTMLElement).style.getPropertyValue('z-index') === '99998') {
            (el as HTMLElement).style.removeProperty('z-index');
          }
        });
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [phase]);

  // Cleanup timer on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const releaseSuppression = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
      document.querySelectorAll('body > div, body > iframe').forEach(el => {
        if ((el as HTMLElement).style.getPropertyValue('z-index') === '99998') {
          (el as HTMLElement).style.removeProperty('z-index');
        }
      });
    }
  }, []);

  const handleGetTickets = () => {
    // Signal checkout intent to Meta — the OnTourHQ iframe is cross-domain,
    // so this is the deepest funnel event the pixel can see client-side.
    (window as any).fbq?.("track", "InitiateCheckout");
    // Open the OnTourHQ checkout directly.
    document.getElementById("modal-trigger-element-id")?.click();
  };

  const progress = startPosition > 0 ? ((startPosition - position) / startPosition) * 100 : 0;
  const overlayVisible = phase !== "idle";

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />
      <TicketsUrgencyBar />

      {/* Hidden modal trigger */}
      <button
        id="modal-trigger-element-id"
        aria-hidden="true"
        style={{ position: "fixed", left: "-9999px", width: "1px", height: "1px", opacity: 0.01 }}
      />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative min-h-fit md:min-h-[80vh] bg-yacht-blue overflow-hidden flex flex-col md:flex-row md:items-end"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{ opacity: 0.15, filter: "brightness(0) invert(1)" }}
        />

        {/* MOBILE LAYOUT */}
        <div className="md:hidden relative z-20 flex flex-col items-center pt-28 pb-8 px-6">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-3 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Secure Your Spot
          </motion.p>
          <div className="text-[8vw] font-bold text-white leading-[0.9] tracking-tight text-center mb-8" role="heading" aria-level={1}>
            <GradualSpacing text="Yacht Rock Detroit" duration={0.5} delayMultiple={0.04} />
            <br />
            <GradualSpacing text="2026 Tickets" duration={0.5} delayMultiple={0.04} />
          </div>
          <div className="relative w-full max-w-xs h-[320px]">
            <motion.div
              className="absolute left-0 top-0 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10"
              initial={{ opacity: 0, y: 30, rotate: -5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <img src={images.hero1} alt="Band performing under dramatic stage lights" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </motion.div>
            <motion.div
              className="absolute right-0 top-8 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-20"
              initial={{ opacity: 0, y: 30, rotate: 5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <img src={images.hero2} alt="Audience with hands raised at the concert" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </motion.div>
          </div>
        </div>

        {/* DESKTOP LAYOUT */}
        <motion.div
          className="hidden md:block absolute top-44 right-[5%] w-[35%] max-w-md aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, y: 50, rotate: 3 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 3 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img src={images.hero1} alt="Band performing under dramatic stage lights" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </motion.div>
        <motion.div
          className="hidden md:block absolute bottom-24 right-[25%] w-[25%] max-w-xs aspect-square rounded-2xl overflow-hidden shadow-2xl z-10"
          initial={{ opacity: 0, y: 30, rotate: -5 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <img src={images.hero2} alt="Audience with hands raised at the concert" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </motion.div>
        <div className="hidden md:block relative z-20 px-8 md:px-16 pb-16 md:pb-24">
          <motion.p
            className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Secure Your Spot
          </motion.p>
          <h1 className="text-[10vw] lg:text-[8vw] font-bold text-white leading-[0.85] tracking-tight">
            <GradualSpacing text="Tickets" duration={0.5} delayMultiple={0.08} />
          </h1>
        </div>
      </section>

      {/* Stage Timeline — clean "where we are" indicator */}
      <PricingStageTimeline />

      {/* TICKET ACCORDION */}
      <section className="bg-yacht-cream py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-yacht-blue text-sm font-semibold tracking-[0.2em] uppercase mb-3">
              July 17–18, 2026 · The Roostertail · Detroit, MI
            </p>
            <h2 className="text-h1 md:text-hero text-yacht-charcoal font-bold mb-4">
              Choose Your Experience
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-6">
              Two days. Multiple stages. One unforgettable weekend on the Detroit River.
            </p>

            {/* Trust banner */}
            <div className="inline-flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-5 py-3 rounded-full bg-yacht-blue/5 border border-yacht-blue/15 max-w-2xl">
              <span className="inline-flex items-center gap-1.5 text-yacht-charcoal text-xs md:text-sm font-semibold">
                <Check className="w-4 h-4 text-yacht-blue" /> Two-Day Pass · Best Value
              </span>
              <span className="inline-flex items-center gap-1.5 text-yacht-charcoal text-xs md:text-sm font-semibold">
                <Check className="w-4 h-4 text-yacht-blue" /> Single Day · Fri or Sat
              </span>
              <span className="inline-flex items-center gap-1.5 text-yacht-charcoal text-xs md:text-sm font-semibold">
                <Check className="w-4 h-4 text-yacht-blue" /> Mobile · just show the QR
              </span>
            </div>
          </motion.div>

          {/* Accordion List — three separate dark cards with spacing */}
          <div className="space-y-4 md:space-y-5">
            <TicketAccordionRow
              tier="Single-Day Passes"
              subLabel="Friday or Saturday"
              newBadge
              customBody={
                <SingleDayBody
                  onBuyFriday={() => handleGetTickets()}
                  onBuySaturday={() => handleGetTickets()}
                />
              }
              isOpen={openTier === "single"}
              onToggle={() => setOpenTier((prev) => (prev === "single" ? null : "single"))}
              onBuy={() => handleGetTickets()}
              delay={0.1}
            />
            <TicketAccordionRow
              tier="General Admission"
              subLabel="Two-Day Pass · Both days for $70 more"
              features={gaFeatures}
              isOpen={openTier === "ga"}
              onToggle={() => setOpenTier((prev) => (prev === "ga" ? null : "ga"))}
              onBuy={() => handleGetTickets()}
              delay={0.2}
            />
            <TicketAccordionRow
              tier="VIP Admiral's Pass"
              subLabel="Two-Day Pass"
              features={vipFeatures}
              isVip
              isOpen={openTier === "vip"}
              onToggle={() => setOpenTier((prev) => (prev === "vip" ? null : "vip"))}
              onBuy={() => handleGetTickets()}
              delay={0.3}
            />
          </div>

          {/* Bottom note — social proof, simple */}
          <motion.div
            className="mt-12 md:mt-16 text-center max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-base md:text-xl font-bold tracking-tight text-yacht-charcoal mb-3">
              <span className="text-red-600">Last year sold out.</span> This year, we added more.
            </p>
            <p className="text-yacht-charcoal/80 text-sm md:text-base font-semibold leading-relaxed">
              Three Dog Night · Yacht Rock Revue · Pablo Cruise · Ambrosia · Elliot Lurie · Peter Beckett · Walter Egan · John Ford Coley
            </p>
            <p className="text-yacht-blue text-[11px] md:text-sm font-bold uppercase tracking-[0.15em] mt-3">
              Joining 25+ live acts across the weekend
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto mt-16 md:mt-20">
            <SponsorsBlock />
          </div>
        </div>
      </section>
      <Footer />

      {/* Queue Overlay */}
      <AnimatePresence>
        {overlayVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="queue-overlay fixed inset-0 z-[99999] flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(120,120,120,0.95)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
          >
            {phase === "queue" && (
              <motion.div
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="fixed top-0 left-0 right-0 z-[100000] text-center py-3 px-4 text-white text-[13px] leading-snug border-b border-white/10"
                style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
              >
                You're in line — we'll hold this price for you. Make sure you have your access code ready. Check your email for it.
              </motion.div>
            )}

            {phase === "queue" && (
              <div className="fixed top-[42px] left-0 right-0 h-0.5 bg-white/10">
                <motion.div
                  className="h-full bg-white/40"
                  initial={{ width: "0%" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </div>
            )}

            <motion.div
              initial={{ scale: 0.97, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.97, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 md:p-12 text-center"
            >
              {phase === "loading" && (
                <div className="py-6">
                  <PulsingDots />
                  <h2 className="text-lg font-semibold text-yacht-charcoal">
                    Loading tickets…
                  </h2>
                </div>
              )}

              {phase === "cleared" && (
                <div className="py-6">
                  <p className="text-2xl mb-3">🎉</p>
                  <h2 className="text-lg font-semibold text-yacht-charcoal">
                    You're up! Loading your tickets…
                  </h2>
                </div>
              )}

              {phase === "queue" && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold text-yacht-charcoal mb-1">
                    We're holding your spot and your early bird price
                  </h2>

                  <PulsingDots />

                  <p className="text-yacht-charcoal/80 text-[15px] leading-relaxed mb-1.5">
                    There are approximately <span className="font-semibold">{position}</span> people ahead of you.
                  </p>
                  <p className="text-yacht-charcoal/55 text-sm mb-6">
                    Estimated wait: {estimatedWait(position)}
                  </p>

                  <div className="border-t border-yacht-charcoal/10 pt-5 space-y-3 text-[13px] text-yacht-charcoal/50 leading-relaxed">
                    <p>⚠️ Do not refresh this tab. You will lose your place in line.</p>
                    <p>Your early pricing is locked. Once you reach checkout, you'll have 10 minutes to complete your order.</p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Tickets;
